import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import moment from "moment";
import { useSnapshot } from "valtio";

import store, {
    setSalesModesCsv,
    setSalesModesExcel,
    setSalesModesTop10Csv,
    setSalesModesTop10Excel,
    setSalesModesAllPercentage,
} from "@pages/Statistics/store";

import { StatisticsContext } from "@pages/Statistics/context";

import { useResource } from "@hooks/useResource";

import {
    capitalize,
    formatCurrency,
    formatExcelData,
    getAdvancedPeriodStringParam,
    getTypeParam,
} from "@helpers/general";

import { DownloadDropdown } from "@pages/Statistics/NewProducts/DownloadDropdown";
import TitleRow from "@components/Common/TitleRow";

import SalesModeCol from "./SalesModeCol";
import StatisticsSalesModeCol from "./StatisticsSalesModeCol";
import CustomErrorBoundary from "@components/Common/CustomErrorBoundary";
import ErrorPage from "@components/Common/ErrorPage";
import GeneralCustomSelectGroup from "@pages/Statistics/NewProducts/GeneralCustomSelectGroup";

export default function SalesModeRow() {
    const { t } = useTranslation();

    const { period, type, advancedDate, selections } = useSelector(
        (state: any) => state
    );

    const {
        top10: {
            percentage: top10Percentage,
            categories: top10Categories,
            histogram: top10Histogram,
        },
        all: { percentage, categories, histogram },
    } = useSnapshot(store.salesModes);

    const [percentageData, setPercentageData] = React.useState<any[]>([]);
    const [histogramData, setHistogramData] = React.useState<any[]>([]);
    const [categoriesData, setCategoriesData] = React.useState<any[]>([]);

    const [salesModesSelection, setSalesModesSelection] = React.useState<
        string[]
    >([t("All sales methods")]);

    const [salesModeListData, setSalesModeListData] = React.useState<string[]>(
        []
    );

    const {
        resourceData: salesModeList,
        error: salesModeListError,
    } = useResource(
        `${
            process.env.REACT_APP_API_V1_URL
        }/statistics/sales-mode-list?${getAdvancedPeriodStringParam(
            period,
            advancedDate
        )}${
            selections.designation && selections.designation !== "my_favorites"
                ? `&selected=${selections.designation}`
                : ""
        }${type === t("Average turnover") ? "" : `&type=${getTypeParam(type)}`}`
    );

    const {
        resourceData: { data: salesModesPercentage },
    } = useResource(
        `${
            process.env.REACT_APP_API_V1_URL
        }/statistics/products/all/percentage-sales-mode?type=${getTypeParam(
            type
        )}&${getAdvancedPeriodStringParam(period, advancedDate)}${
            selections.designation && selections.designation !== "my_favorites"
                ? `&selected=${selections.designation}`
                : ""
        }`
    );

    React.useEffect(() => {
        let localSalesModeList: string[] = [];

        salesModeList.data?.forEach((el: string) => {
            localSalesModeList.push(t(capitalize(el)));
        });

        setSalesModeListData(localSalesModeList);
    }, [salesModeList, t]);

    React.useEffect(() => {
        let array: any[] = [];

        const headers: any[] = [t("Categories")];

        top10Histogram.forEach((datum: any) => {
            headers.push(t(capitalize(datum.designation.toLowerCase())));
        });

        let averageLine: any[] = [];

        top10Categories.forEach((category: any) => {
            let localArray: any[] = [];
            let localCategory = category;

            top10Histogram.forEach((item: any, index: number) => {
                item.value.forEach((datum: any) => {
                    let localDesignation = String(datum.designation);

                    if (
                        advancedDate.startDate !== "" &&
                        advancedDate.endDate !== ""
                    ) {
                        let daysBetweenDates = moment
                            .unix(Number(advancedDate.endDate))
                            .diff(
                                moment.unix(Number(advancedDate.startDate)),
                                "days"
                            );
                        if (daysBetweenDates === 0) {
                            localDesignation = String(datum.designation);
                        } else if (daysBetweenDates <= 31) {
                            localDesignation = capitalize(
                                moment
                                    .unix(Number(datum.designation))
                                    .format("ddd DD-MM-YYYY")
                            );
                        } else {
                            localDesignation = capitalize(
                                moment
                                    .unix(Number(datum.designation))
                                    .format("MMM YYYY")
                            );
                        }
                    } else if (
                        [
                            t("Current week"),
                            t("The last 7 days"),
                            t("The last 14 days"),
                            t("The last 30 days"),
                        ].includes(period)
                    ) {
                        localDesignation = capitalize(
                            moment
                                .unix(Number(datum.designation))
                                .format("ddd DD-MM-YYYY")
                        );
                        localCategory = capitalize(
                            moment
                                .unix(Number(category))
                                .format("ddd DD-MM-YYYY")
                        );
                    } else if (period === t("Current month")) {
                        localDesignation = capitalize(
                            moment.unix(Number(datum.designation)).format("DD")
                        );
                    } else if (period === t("Current year")) {
                        localDesignation = capitalize(
                            moment
                                .unix(Number(datum.designation))
                                .format("MMM YYYY")
                        );
                    } else if (
                        [
                            t("The last 90 days"),
                            t("The last 180 days"),
                            t("The last 365 days"),
                        ].includes(period)
                    ) {
                        localDesignation = capitalize(
                            moment
                                .unix(Number(datum.designation))
                                .format("MMM YYYY")
                        );
                    }

                    if (localDesignation === localCategory) {
                        localArray.push(
                            type !== t("Tickets")
                                ? datum.value
                                : `${datum.value} ${t("Tickets")}`
                        );
                    }
                });

                if (!localArray[index]) {
                    localArray[index] = 0;
                }
            });

            array.push([localCategory, ...localArray]);
        });

        top10Percentage.forEach((datum: number) => {
            const average = array.length === 0 ? 0 : datum / array.length;
            averageLine.push(
                type !== t("Tickets")
                    ? formatCurrency(average)
                    : average.toFixed(2)
            );
        });

        const formattedPercentageData = top10Percentage.map((el: number) => {
            return type !== t("Tickets") ? formatCurrency(el) : el;
        });

        setSalesModesTop10Csv([
            headers,
            ...array,
            [t("Total"), ...formattedPercentageData],
            [t("Average"), ...averageLine],
        ]);

        setSalesModesTop10Excel([
            formatExcelData(
                headers,
                array,
                formattedPercentageData,
                averageLine,
                type
            ),
        ]);
    }, [
        t,
        top10Histogram,
        top10Categories,
        top10Percentage,
        advancedDate.startDate,
        advancedDate.endDate,
        period,
        type,
    ]);

    React.useEffect(() => {
        setSalesModesAllPercentage(
            salesModesPercentage.map((item: any) => item.value)
        );
    }, [salesModesPercentage]);

    React.useEffect(() => {
        let localProducts: number[] = [];

        salesModesPercentage.forEach((item: any) => {
            localProducts.push(item.value);
        });

        let array: any[] = [];

        let headers: any[] = [t("Categories")];

        histogram.forEach((datum: any) => {
            headers.push(t(capitalize(datum.designation.toLowerCase())));
        });

        categories.forEach((category: any) => {
            let localArray: any[] = [];
            let localCategory = category;

            histogram.forEach((item: any, index: number) => {
                item.value.forEach((datum: any) => {
                    let localDesignation = String(datum.designation);

                    if (
                        advancedDate.startDate !== "" &&
                        advancedDate.endDate !== ""
                    ) {
                        let daysBetweenDates = moment
                            .unix(Number(advancedDate.endDate))
                            .diff(
                                moment.unix(Number(advancedDate.startDate)),
                                "days"
                            );
                        if (daysBetweenDates === 0) {
                            localDesignation = String(datum.designation);
                        } else if (daysBetweenDates <= 31) {
                            localDesignation = capitalize(
                                moment
                                    .unix(Number(datum.designation))
                                    .format("ddd DD-MM-YYYY")
                            );
                        } else {
                            localDesignation = capitalize(
                                moment
                                    .unix(Number(datum.designation))
                                    .format("MMM YYYY")
                            );
                        }
                    } else if (
                        [
                            t("Current week"),
                            t("The last 7 days"),
                            t("The last 14 days"),
                            t("The last 30 days"),
                        ].includes(period)
                    ) {
                        localDesignation = capitalize(
                            moment
                                .unix(Number(datum.designation))
                                .format("ddd DD-MM-YYYY")
                        );
                        localCategory = capitalize(
                            moment
                                .unix(Number(category))
                                .format("ddd DD-MM-YYYY")
                        );
                    } else if (period === t("Current month")) {
                        localDesignation = capitalize(
                            moment.unix(Number(datum.designation)).format("DD")
                        );
                    } else if (period === t("Current year")) {
                        localDesignation = capitalize(
                            moment
                                .unix(Number(datum.designation))
                                .format("MMM YYYY")
                        );
                    } else if (
                        [
                            t("The last 90 days"),
                            t("The last 180 days"),
                            t("The last 365 days"),
                        ].includes(period)
                    ) {
                        localDesignation = capitalize(
                            moment
                                .unix(Number(datum.designation))
                                .format("MMM YYYY")
                        );
                    }

                    if (localDesignation === localCategory) {
                        localArray.push(
                            type !== t("Tickets")
                                ? datum.value
                                : `${datum.value} ${t("Tickets")}`
                        );
                    }
                });
                if (!localArray[index]) {
                    localArray[index] = 0;
                }
            });

            array.push([localCategory, ...localArray]);
        });

        const averageLine: string[] = [];

        (percentage.length > 0 ? percentage : localProducts).forEach(
            (datum: number) => {
                averageLine.push(
                    type !== t("Tickets")
                        ? formatCurrency(datum / array.length)
                        : (datum / array.length).toFixed(2)
                );
            }
        );

        const formattedPercentageData = (percentage.length > 0
            ? percentage
            : localProducts
        ).map((element: number) => {
            return type !== t("Tickets") ? formatCurrency(element) : element;
        });

        const localCsvProducts = [
            headers,
            ...array,
            [t("Total"), ...formattedPercentageData],
            [t("Average"), ...averageLine],
        ];

        setSalesModesCsv(localCsvProducts);

        setSalesModesExcel([
            formatExcelData(
                headers,
                array,
                formattedPercentageData,
                averageLine,
                type
            ),
        ]);
    }, [
        salesModesPercentage,
        t,
        percentage,
        histogram,
        categories,
        advancedDate.startDate,
        advancedDate.endDate,
        period,
        type,
    ]);

    return (
        <React.Fragment>
            <StatisticsContext.Provider
                value={{
                    period,
                    type,
                    advancedDate,
                    selections,
                    percentageData,
                    setPercentageData,
                    histogramData,
                    setHistogramData,
                    categoriesData,
                    setCategoriesData,
                    salesModesSelection,
                    setSalesModesSelection,
                }}
            >
                <TitleRow title={t("Articles per sales modes")}>
                    {salesModeListError ? (
                        <ErrorPage />
                    ) : (
                        <CustomErrorBoundary>
                            <GeneralCustomSelectGroup
                                options={[
                                    t("All sales methods"),
                                    ...salesModeListData,
                                ]}
                            />
                        </CustomErrorBoundary>
                    )}
                    <DownloadDropdown />
                </TitleRow>
                <div
                    className="d-grid__clz 
                                grid-gap__clz 
                                grid-xl-statistics-products-details__clz 
                                grid-lg-statistics-template-details__clz 
                                grid-md-statistics-template-details__clz 
                                grid-sm-template-col__clz grid-xs-template-col__clz 
                                pr-0"
                >
                    <CustomErrorBoundary>
                        <SalesModeCol />
                    </CustomErrorBoundary>
                    <CustomErrorBoundary>
                        <StatisticsSalesModeCol />
                    </CustomErrorBoundary>
                </div>
            </StatisticsContext.Provider>
        </React.Fragment>
    );
}
